import React, { useState, useEffect } from 'react';
import { X, Settings, Clock, Calendar, Play } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { WorkflowDefinition } from '../../types/workflows';
import { useToast } from '../../hooks/useToast';

interface WorkflowSettingsPanelProps {
  workflow: WorkflowDefinition;
  onUpdate: (workflow: WorkflowDefinition) => void;
  onClose: () => void;
}

const TRIGGER_TYPES = {
  MANUAL: { label: 'Manual', icon: Play, description: 'Manually triggered workflows' },
  INTERVAL: { label: 'Interval', icon: Clock, description: 'Run at regular intervals' },
  CRON: { label: 'Cron Schedule', icon: Calendar, description: 'Run on a cron schedule' }
};

const INTERVAL_UNITS = {
  minutes: 'Minutes',
  hours: 'Hours', 
  days: 'Days'
};

const CRON_PRESETS = {
  '0 9 * * 1-5': 'Every weekday at 9 AM',
  '0 0 * * 0': 'Every Sunday at midnight',
  '0 */2 * * *': 'Every 2 hours',
  '0 0 */7 * *': 'Every 7 days at midnight',
  '0 8 1 * *': 'First day of every month at 8 AM'
};

export const WorkflowSettingsPanel: React.FC<WorkflowSettingsPanelProps> = ({
  workflow,
  onUpdate,
  onClose
}) => {
  const { showToast } = useToast();
  const [name, setName] = useState(workflow.name || '');
  const [triggerType, setTriggerType] = useState(workflow.trigger_type || 'MANUAL');
  const [cronExpression, setCronExpression] = useState('');
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState('hours');
  const [isActive, setIsActive] = useState(workflow.is_active || false);

  useEffect(() => {
    if (workflow.trigger_config) {
      if (triggerType === 'CRON' && workflow.trigger_config.cron) {
        setCronExpression(workflow.trigger_config.cron);
      } else if (triggerType === 'INTERVAL' && workflow.trigger_config.interval_seconds) {
        // Convert seconds back to a reasonable unit for display
        const seconds = workflow.trigger_config.interval_seconds;
        if (seconds % 86400 === 0) {
          setIntervalValue(seconds / 86400);
          setIntervalUnit('days');
        } else if (seconds % 3600 === 0) {
          setIntervalValue(seconds / 3600);
          setIntervalUnit('hours');
        } else {
          setIntervalValue(Math.max(1, seconds / 60));
          setIntervalUnit('minutes');
        }
      }
    }
  }, [workflow.trigger_config, triggerType]);

  const validateCronExpression = (expression: string): boolean => {
    if (!expression.trim()) return false;
    const parts = expression.trim().split(/\s+/);
    return parts.length === 5;
  };

  const handleSave = () => {
    let updatedTriggerConfig = {};
    
    if (triggerType === 'CRON') {
      if (!validateCronExpression(cronExpression)) {
        showToast('Please enter a valid cron expression (5 parts: minute hour day month weekday)', 'error');
        return;
      }
      updatedTriggerConfig = { cron: cronExpression };
    } else if (triggerType === 'INTERVAL') {
      if (intervalValue <= 0) {
        showToast('Interval value must be greater than 0', 'error');
        return;
      }
      // Convert to seconds as expected by backend
      const unitMultipliers = { minutes: 60, hours: 3600, days: 86400 };
      const intervalSeconds = intervalValue * unitMultipliers[intervalUnit as keyof typeof unitMultipliers];
      
      // Backend validation requires minimum 60 seconds
      if (intervalSeconds < 60) {
        showToast('Interval must be at least 1 minute', 'error');
        return;
      }
      
      updatedTriggerConfig = { interval_seconds: intervalSeconds };
    }

    const updatedWorkflow: WorkflowDefinition = {
      ...workflow,
      name: name.trim() || 'Untitled Workflow',
      trigger_type: triggerType,
      trigger_config: updatedTriggerConfig,
      is_active: isActive
    };

    onUpdate(updatedWorkflow);
    onClose();
  };

  const handlePresetSelect = (preset: string) => {
    setCronExpression(preset);
  };

  return (
    <div className="absolute left-0 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-30 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Settings className="w-5 h-5 text-blue-600" />
          <h3 className="font-medium text-gray-900">Workflow Settings</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Basic Information</h4>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Workflow Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter workflow name"
            />
          </div>

        </div>

        {/* Trigger Configuration */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Trigger Configuration</h4>
          
          {/* Trigger Type Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Trigger Type
            </label>
            <div className="space-y-2">
              {Object.entries(TRIGGER_TYPES).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <label key={type} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="triggerType"
                      value={type}
                      checked={triggerType === type}
                      onChange={(e) => setTriggerType(e.target.value)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <Icon className="w-4 h-4 text-gray-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-700">{config.label}</div>
                      <div className="text-xs text-gray-500">{config.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Cron Configuration */}
          {triggerType === 'CRON' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Cron Expression
                </label>
                <Input
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  placeholder="0 9 * * 1-5"
                  className={`${!validateCronExpression(cronExpression) && cronExpression ? 'border-red-500' : ''}`}
                />
                <p className="text-xs text-gray-500">
                  Format: minute hour day month weekday
                </p>
                {!validateCronExpression(cronExpression) && cronExpression && (
                  <p className="text-xs text-red-600">
                    Invalid cron expression. Must have 5 parts.
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Common Presets
                </label>
                <div className="space-y-1">
                  {Object.entries(CRON_PRESETS).map(([preset, description]) => (
                    <button
                      key={preset}
                      onClick={() => handlePresetSelect(preset)}
                      className="w-full text-left text-xs p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <div className="font-medium text-gray-700">{description}</div>
                      <div className="text-gray-500">{preset}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Interval Configuration */}
          {triggerType === 'INTERVAL' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Run Every
              </label>
              <div className="flex space-x-2">
                <Input
                  type="number"
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  className="flex-1"
                />
                <select
                  value={intervalUnit}
                  onChange={(e) => setIntervalUnit(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(INTERVAL_UNITS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Manual Trigger Info */}
          {triggerType === 'MANUAL' && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                This workflow will only run when manually triggered.
              </p>
            </div>
          )}
        </div>

        {/* Workflow Status */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Status</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Active</span>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <p className="text-xs text-gray-500">
              {isActive 
                ? 'Workflow will run according to its trigger schedule' 
                : 'Workflow is paused and will not run automatically'}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        <Button
          onClick={handleSave}
          className="w-full"
        >
          Save Settings
        </Button>
        <Button
          variant="ghost"
          onClick={onClose}
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};